import { useEffect, useRef, useState } from "react";
import { Bookmark, ChevronLeft, ChevronRight } from "lucide-react";
import { Image } from "@/components/ui/image";
import HeroThumbs from "@/components/yts/business/HeroThumbs";
import ListingPhotoPlaceholder from "@/components/yts/ListingPhotoPlaceholder";
import { useLang } from "@/lib/i18n";

const ADVANCE_MS = 5200;

/**
 * The business card's photo gallery: one large cover that advances on its own,
 * with the rest of the album in a strip directly beneath it — the pictures read
 * as one continuous gallery instead of a cover followed by a grid much further
 * down the page.
 */
export default function DetailHero({ photos = [], title, category, saved, onToggleSave, onOpen, saveLabel }) {
  const [active, setActive] = useState(0);
  const [failed, setFailed] = useState(false);
  const { lang } = useLang();
  const [paused, setPaused] = useState(false);
  const [onScreen, setOnScreen] = useState(true);
  const [tabVisible, setTabVisible] = useState(true);
  const [reduced, setReduced] = useState(false);
  const rootRef = useRef(null);
  const touchStart = useRef(null);
  const swiped = useRef(false);

  const count = photos.length;
  const current = count ? Math.min(active, count - 1) : 0;

  useEffect(() => setActive(0), [photos[0]]);
  // Reset the fallback state if a visitor selects another image in the album.
  useEffect(() => setFailed(false), [photos[current]]);

  // Only advance while the gallery is on screen and the page is being watched.
  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setOnScreen(Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.2)),
      { threshold: [0, 0.2, 0.6] }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const update = () => setTabVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", update);
    update();
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    if (count < 2 || !onScreen || !tabVisible || paused || reduced) return undefined;
    const timer = window.setInterval(() => setActive((previous) => (previous + 1) % count), ADVANCE_MS);
    return () => window.clearInterval(timer);
  }, [count, onScreen, tabVisible, paused, reduced]);

  const step = (delta) => setActive((previous) => (previous + delta + count) % count);

  const openCurrent = () => {
    // A swipe across the cover is a gesture, not a tap: don't open the viewer.
    if (swiped.current) {
      swiped.current = false;
      return;
    }
    onOpen?.(current);
  };

  const handleTouchEnd = (event) => {
    const from = touchStart.current;
    touchStart.current = null;
    if (from == null || count < 2) return;
    const delta = (event.changedTouches[0]?.clientX ?? from) - from;
    if (Math.abs(delta) < 44) return;
    swiped.current = true;
    step(delta < 0 ? 1 : -1);
  };

  return (
    <section
      ref={rootRef}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
      }}
    >
      <div className="relative overflow-hidden rounded-[2px] bg-[#eceae5] dark:bg-[#242520]">
        <button
          type="button"
          onClick={count > 0 && !failed ? openCurrent : undefined}
          onTouchStart={(event) => { touchStart.current = event.touches[0]?.clientX ?? null; }}
          onTouchEnd={handleTouchEnd}
          className={`block aspect-[5/4] w-full [touch-action:pan-y] sm:aspect-[16/10] ${count > 0 && !failed ? "cursor-zoom-in" : "cursor-default"}`}
          aria-label={count > 1 ? `${title} — photo ${current + 1} of ${count}` : title}
        >
          {count > 0 && !failed ? (
            <Image
              key={photos[current]}
              src={photos[current]}
              alt={title}
              fittingType="fill"
              onError={() => setFailed(true)}
              className={`h-full w-full object-cover ${reduced ? "" : "yts-hero-swap"}`}
            />
          ) : (
            <ListingPhotoPlaceholder item={{ name: title, category }} lang={lang} theme={null} />
          )}
        </button>

        {category && (
          <span className="pointer-events-none absolute left-3 top-3 rounded-[2px] bg-[#1A1A1A]/80 px-3 py-2 text-[10px] font-bold uppercase tracking-[.11em] text-white backdrop-blur-sm">
            {category}
          </span>
        )}

        <button
          type="button"
          onClick={onToggleSave}
          aria-label={saveLabel}
          aria-pressed={saved}
          className="absolute right-3 top-3 grid h-10 w-10 place-items-center rounded-[2px] border border-white/70 bg-white/95 shadow-[0_2px_8px_rgba(0,0,0,0.1)] transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#171717]"
        >
          <Bookmark className="h-[18px] w-[18px]" fill={saved ? "#E11D2E" : "none"} color="#E11D2E" strokeWidth={2} />
        </button>

        {count > 1 && !failed && (
          <div className="absolute bottom-2.5 right-2.5 flex items-center rounded-full border border-white/25 bg-black/55 p-0.5 text-white backdrop-blur-md">
            <button
              type="button"
              onClick={() => step(-1)}
              aria-label="Previous photo"
              className="grid h-8 w-8 place-items-center rounded-full transition hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[2.6rem] text-center text-[10.5px] font-bold tabular-nums">
              {current + 1} / {count}
            </span>
            <button
              type="button"
              onClick={() => step(1)}
              aria-label="Next photo"
              className="grid h-8 w-8 place-items-center rounded-full transition hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {count > 1 && <HeroThumbs photos={photos} active={current} onPick={setActive} />}
    </section>
  );
}